import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, ArrowLeft, ArrowRightLeft, X, CheckCircle2,
    AlertTriangle, MessageSquare, Calendar, User,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import AthleteNameLink from "@/components/coach/AthleteNameLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_LABEL = { switch: "Switch plan", cancel: "Cancel" };
const KIND_TONE = {
    switch: "bg-blue-900/40 text-blue-300",
    cancel: "bg-amber-900/40 text-amber-300",
};

export default function ChangeRequests() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(true);
    const [acting, setActing] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role !== "owner") {
            toast.error("Owner-only");
            navigate("/coach", { replace: true });
        }
    }, [loading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/subscription-change-requests?status=pending`, { withCredentials: true });
            setRows(r.data.requests || []);
        } catch (e) {
            toast.error("Couldn't load requests", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => { if (user?.role === "owner") load(); }, [user]);

    const resolve = async (id, action, note = null) => {
        setActing(id);
        try {
            await axios.post(`${API}/coach/subscription-change-requests/${id}/resolve`, { action, note }, { withCredentials: true });
            toast.success(action === "denied" ? "Marked as denied" : "Marked as resolved");
            load();
        } catch (e) {
            toast.error("Couldn't resolve", { description: e?.response?.data?.detail });
        } finally {
            setActing(null);
        }
    };

    const applyAndComplete = async (req) => {
        if (!window.confirm(
            req.kind === "cancel"
                ? `Cancel ${req.athlete_name}'s subscription at the end of their current billing period?`
                : `Switch ${req.athlete_name} to ${req.target_sku?.display_name || req.target_sku_id}? The new price takes effect at the next billing cycle.`
        )) return;
        setActing(req.request_id);
        try {
            if (req.kind === "cancel") {
                await axios.post(`${API}/coach/athletes/${req.athlete_id}/subscription/cancel`,
                    { reason: req.note || null, request_id: req.request_id },
                    { withCredentials: true });
                toast.success("Cancelled at period end");
            } else {
                await axios.post(`${API}/coach/athletes/${req.athlete_id}/subscription/switch`,
                    { target_sku_id: req.target_sku_id, request_id: req.request_id },
                    { withCredentials: true });
                toast.success("Plan switched — effective next cycle");
            }
            load();
        } catch (e) {
            toast.error("Couldn't apply change", { description: e?.response?.data?.detail });
        } finally {
            setActing(null);
        }
    };

    if (loading || !user || busy) {
        return <div className="min-h-screen bg-black text-white flex items-center justify-center"><Loader2 className="w-7 h-7 text-emerald-400 animate-spin" /></div>;
    }

    return (
        <div data-testid="coach-change-requests" className="min-h-screen bg-black text-white">
            <div className="max-w-4xl mx-auto px-6 py-10">
                <Link to="/coach" className="text-zinc-400 hover:text-white text-sm flex items-center gap-2 mb-6" data-testid="cr-back">
                    <ArrowLeft className="w-4 h-4" /> Back to coach
                </Link>
                <div className="flex items-center gap-3 mb-2">
                    <ArrowRightLeft className="w-7 h-7 text-emerald-400" />
                    <h1 className="text-3xl font-bold">Subscription change requests</h1>
                </div>
                <p className="text-zinc-400 text-sm mb-8 max-w-2xl">
                    Parents who've asked to switch plans or cancel. Click <b>Apply &amp; resolve</b> to push the change to Stripe (no charge today — changes apply at next billing cycle).
                </p>

                {rows.length === 0 ? (
                    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-12 text-center" data-testid="cr-empty">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3 opacity-60" />
                        <p className="text-zinc-400">No pending change requests.</p>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="cr-list">
                        {rows.map((r) => (
                            <div key={r.request_id} className="bg-zinc-950 border border-zinc-800 rounded-lg p-5" data-testid={`cr-card-${r.request_id}`}>
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`px-2 py-0.5 rounded text-xs ${KIND_TONE[r.kind]}`}>{KIND_LABEL[r.kind]}</span>
                                            {r.kind === "switch" && r.target_sku && (
                                                <span className="text-xs text-zinc-300">→ {r.target_sku.display_name} (${(r.target_sku.price_cents / 100).toFixed(2)})</span>
                                            )}
                                        </div>
                                        <h3 className="text-lg font-semibold">
                                            <AthleteNameLink
                                                athleteId={r.athlete_id}
                                                name={r.athlete_name}
                                                testid={`cr-athlete-name-${r.request_id}`}
                                                className="text-lg font-semibold text-white hover:text-brand-red underline-offset-2 hover:underline transition-colors"
                                            />
                                        </h3>
                                        <p className="text-sm text-zinc-400 flex items-center gap-2 mt-0.5">
                                            <User className="w-3 h-3" /> {r.user_name} · {r.user_email}
                                        </p>
                                        <p className="text-xs text-zinc-500 flex items-center gap-1 mt-1">
                                            <Calendar className="w-3 h-3" /> {new Date(r.created_at).toLocaleString()}
                                        </p>
                                    </div>
                                </div>

                                {r.note && (
                                    <div className="bg-zinc-900/60 border border-zinc-800 rounded p-3 mb-3 flex gap-2">
                                        <MessageSquare className="w-4 h-4 text-zinc-500 flex-shrink-0 mt-0.5" />
                                        <p className="text-sm text-zinc-300 whitespace-pre-wrap">{r.note}</p>
                                    </div>
                                )}

                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const note = window.prompt("Deny reason (optional)?", "");
                                            if (note === null) return;
                                            resolve(r.request_id, "denied", note);
                                        }}
                                        disabled={acting === r.request_id}
                                        className="px-3 py-2 text-sm text-zinc-400 hover:text-red-400 disabled:opacity-50"
                                        data-testid={`cr-deny-${r.request_id}`}
                                    >
                                        Deny
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => resolve(r.request_id, "completed", "Resolved manually")}
                                        disabled={acting === r.request_id}
                                        className="px-3 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 rounded"
                                        data-testid={`cr-resolve-${r.request_id}`}
                                    >
                                        Mark resolved
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => applyAndComplete(r)}
                                        disabled={acting === r.request_id || (r.kind === "switch" && !r.target_sku?.display_name)}
                                        className="px-4 py-2 text-sm bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded flex items-center gap-2"
                                        data-testid={`cr-apply-${r.request_id}`}
                                    >
                                        {acting === r.request_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
                                        Apply &amp; resolve
                                    </button>
                                </div>

                                {r.kind === "switch" && !r.target_sku && (
                                    <p className="text-xs text-amber-400 flex items-center gap-1 mt-2">
                                        <AlertTriangle className="w-3 h-3" /> Target plan no longer available — review with parent.
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
